import { z } from 'zod';

/**
 * Abstract base class providing validation capabilities for model objects.
 * Contains static methods for creating validators that parse and validate
 * input data against Zod schemas, throwing errors when validation fails.
 */
export abstract class ValidatableModel {
  /**
   * Creates a validator function that parses and validates input values against a given Zod schema.
   *
   * @param schema - The Zod schema to use for validation and parsing
   * @return A validator function that takes unknown input and returns type T, throwing an error if validation fails
   */
  protected static createValidator<T>(schema: z.ZodSchema<T>) {
    return (value: unknown): T => {
      try {
        return schema.parse(value);
      } catch (e) {
        // zod is not logging any error in the loader
        console.error(e);
        throw e;
      }
    };
  }

  /**
   * Creates a validator function that checks if a value is an array containing elements matching the provided schema.
   *
   * @param schema - The Zod schema that each element in the array must match
   * @return A validator function that takes an unknown value and returns a parsed array of type T[]
   */
  protected static createArrayValidator<T>(schema: z.ZodSchema<T>) {
    return (value: unknown): T[] => {
      try {
        const arraySchema = z.array(schema);
        return arraySchema.parse(value);
      } catch (e) {
        // zod is not logging any error in the loader
        console.error(e);
        throw e;
      }
    };
  }
}
